import {
  Component,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TermsPrivacyComponent } from '../terms-privacy/terms-privacy.component';
import { WhitelabelService } from '../whitelabel.service';
import { ClientStore } from 'src/app/stores/stores';

/**
 * Reusable Terms & Conditions acceptance control.
 *
 * Renders one of three visual modes based on the selected store's
 * `TERMS_AND_CONDITIONS_ACCEPTANCE` setting:
 *  - EXPLICIT: a required checkbox plus disclaimer text with embedded
 *    Terms / Privacy / Refund links.
 *  - IMPLICIT: the same disclaimer text with no checkbox.
 *  - HIDDEN: renders nothing at all; no form control or validator is
 *    registered, so checkout is never gated on terms acceptance.
 *
 * Defaults to IMPLICIT when the setting is missing or unknown.
 *
 * The component owns a boolean `termsAccepted` control that it registers
 * on the parent `FormGroup` passed via `[parentForm]`. It applies
 * `Validators.requiredTrue` only in EXPLICIT mode so submission gating is
 * automatic via the parent form's validity. In IMPLICIT and HIDDEN modes
 * the control carries no validator.
 *
 * Link rendering (Terms / Privacy / Refund) and the "Read More" modal
 * are delegated to the existing `TermsPrivacyComponent` so the URL
 * resolution rules (store custom URL → whitelabel fallback) stay in
 * one place.
 */
@Component({
  selector: 'app-terms-acceptance',
  templateUrl: './terms-acceptance.component.html',
  styleUrls: ['./terms-acceptance.component.scss'],
})
export class TermsAcceptanceComponent implements OnInit, OnChanges, OnDestroy {
  /** Parent form the `termsAccepted` control is registered on. */
  @Input() parentForm!: FormGroup;

  /** Selected store providing legal settings. */
  @Input() selectedStore: ClientStore;

  /** Name of the boolean control on `parentForm`. Defaults to `termsAccepted`. */
  @Input() controlName = 'termsAccepted';

  /**
   * Optional flag the host can set to surface validation errors after a
   * submit attempt even when the control has not been touched yet.
   */
  @Input() submitted = false;

  @ViewChild('termsPrivacyComponent', { static: true })
  termsPrivacyComponent: TermsPrivacyComponent;

  /** Tracks whether this component instance added the control to the
   * parent form (vs. found one already declared by the host). The
   * component only removes a control it added, so hosts that declare
   * their own (e.g. checkout's `termsAcceptedForm`) keep ownership. */
  private addedControl = false;

  /** Collapse the host element completely in HIDDEN mode so no vertical
   * gap is left behind in any of the six host layouts. The parent form's
   * validator is also cleared by syncControl(), so neither visual nor
   * validation traces remain when the acceptance block is suppressed. */
  @HostBinding('style.display')
  get hostDisplay(): string {
    return this.isHidden() ? 'none' : '';
  }

  constructor(public whitelabelService: WhitelabelService) {}

  ngOnInit(): void {
    this.syncControl();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedStore'] || changes['parentForm']) {
      this.syncControl();
    }
  }

  /**
   * Ensure the `termsAccepted` control exists on the parent form with
   * validators that match the current acceptance mode. Idempotent — if
   * the host already created the control (legacy callers), the validator
   * is reconciled in place rather than the control being replaced.
   *
   * In HIDDEN mode no control is added; if a host pre-declared the control
   * its validators are cleared so it never blocks form submission.
   */
  private syncControl(): void {
    if (!this.parentForm) {
      return;
    }
    const existing = this.parentForm.get(this.controlName);
    const validators = this.isExplicit() ? [Validators.requiredTrue] : [];

    if (existing) {
      existing.setValidators(validators);
      existing.updateValueAndValidity({ emitEvent: false });
    } else if (!this.isHidden()) {
      this.parentForm.addControl(
        this.controlName,
        new FormControl(false, validators),
      );
      this.addedControl = true;
    }
  }

  ngOnDestroy(): void {
    // Only remove the control if this component added it. Hosts that
    // pre-declared the control (e.g. checkout's termsAcceptedForm) keep
    // owning it across the component's lifecycle.
    if (this.addedControl && this.parentForm?.contains(this.controlName)) {
      this.parentForm.removeControl(this.controlName);
    }
  }

  isExplicit(): boolean {
    return (
      this.selectedStore?.settings?.TERMS_AND_CONDITIONS_ACCEPTANCE ===
      'EXPLICIT'
    );
  }

  isImplicit(): boolean {
    const mode = this.selectedStore?.settings?.TERMS_AND_CONDITIONS_ACCEPTANCE;
    return mode === 'IMPLICIT' || (mode !== 'EXPLICIT' && mode !== 'HIDDEN');
  }

  isHidden(): boolean {
    return (
      this.selectedStore?.settings?.TERMS_AND_CONDITIONS_ACCEPTANCE === 'HIDDEN'
    );
  }

  get acceptanceControl(): FormControl | null {
    return (this.parentForm?.get(this.controlName) as FormControl) ?? null;
  }

  /** Whether the validation error message should be visible. */
  showError(): boolean {
    if (!this.isExplicit()) {
      return false;
    }
    const control = this.acceptanceControl;
    if (!control) {
      return false;
    }
    return control.invalid && (control.touched || this.submitted);
  }

  getEffectiveTermsUrl(): string {
    return (
      this.selectedStore?.settings?.CUSTOM_TERMS_URL ||
      this.whitelabelService.getWhiteLabelTermsLink()
    );
  }

  getEffectivePrivacyUrl(): string {
    return (
      this.selectedStore?.settings?.CUSTOM_PRIVACY_URL ||
      this.whitelabelService.getWhiteLabelPrivacyLink()
    );
  }

  getEffectiveRefundUrl(): string {
    return (
      this.selectedStore?.settings?.CUSTOM_REFUND_URL ||
      this.whitelabelService.getWhiteLabelRefundsLink()
    );
  }
}
